import { create } from "zustand";
import { persist } from "zustand/middleware";
import { initialsFromName } from "@/lib/media";

export type FavouriteContact = {
  /** Normalized phone digits used as stable key */
  key: string;
  name: string;
  number: string;
  initials: string;
  contactId?: string;
};

type FavouritesState = {
  items: FavouriteContact[];
  isFavourite: (phone: string) => boolean;
  toggleFavourite: (input: {
    name: string;
    number: string;
    contactId?: string;
  }) => void;
  removeFavourite: (phone: string) => void;
};

export function favouriteKeyFromPhone(phone: string) {
  return phone.replace(/\D/g, "") || phone.trim();
}

export const useFavouritesStore = create<FavouritesState>()(
  persist(
    (set, get) => ({
      items: [],
      isFavourite: (phone) => {
        const key = favouriteKeyFromPhone(phone);
        if (!key) return false;
        return get().items.some((item) => item.key === key);
      },
      toggleFavourite: ({ name, number, contactId }) => {
        const key = favouriteKeyFromPhone(number);
        if (!key) return;
        set((state) => {
          const exists = state.items.some((item) => item.key === key);
          if (exists) {
            return { items: state.items.filter((item) => item.key !== key) };
          }
          const entry: FavouriteContact = {
            key,
            name: name.trim() || number,
            number: number.trim() || key,
            initials: initialsFromName(name.trim() || number),
            contactId,
          };
          return { items: [entry, ...state.items] };
        });
      },
      removeFavourite: (phone) => {
        const key = favouriteKeyFromPhone(phone);
        if (!key) return;
        set((state) => ({
          items: state.items.filter((item) => item.key !== key),
        }));
      },
    }),
    {
      name: "papaya-web-favourites",
      partialize: (state) => ({ items: state.items }),
    },
  ),
);
